<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppMessage from '@uiKit/elements/Message.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import { formatAed } from '@shared/utils'

// PAYMENT METHOD (Figma 32590:66230). The wallet is the only live rail; the
// card option is shown as designed but flags itself unavailable when picked
// and the order can't be paid with it.

export type PaymentMethod = 'wallet' | 'gateway'

interface Props {
  modelValue: PaymentMethod
  walletBalance: number
  /** Total payable for the current quote. */
  orderTotal: number
  exceedsBalance: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentMethod): void
}>()

interface PaymentOption {
  key: PaymentMethod
  icon: PiIconName
  title: string
}

const OPTIONS: PaymentOption[] = [
  { key: 'wallet', icon: 'wallet', title: 'Platform Wallet' },
  { key: 'gateway', icon: 'credit-card', title: 'Debit / Credit Card' },
]

const balanceAfter = computed(() => Math.max(props.walletBalance - props.orderTotal, 0))
</script>

<template>
  <Box direction="col" gap="4">
    <Text type="body" weight="semibold" is="h3">PAYMENT METHOD</Text>

    <Box direction="col" gap="10px" role="radiogroup" aria-label="Payment method">
      <Box
        v-for="option in OPTIONS"
        :key="option.key"
        is="button"
        type="button"
        role="radio"
        direction="col"
        gap="2"
        class="payment__option"
        :class="{ 'payment__option--active': modelValue === option.key }"
        :aria-checked="modelValue === option.key"
        @click="emit('update:modelValue', option.key)"
      >
        <Box align="center" gap="4" block>
          <PiIcon :name="option.icon" class="payment__icon" />
          <Box direction="col" grow class="payment__text">
            <Text type="heading" weight="bold" is="span" style="line-height: 24px">{{
              option.title
            }}</Text>
            <Text type="body" color="var(--p-surface-600)" is="span">
              <CurrencyText
                :text="
                  option.key === 'wallet'
                    ? `Balance: ${formatAed(walletBalance)}`
                    : 'Choose card as mode of payment'
                "
              />
            </Text>
          </Box>
          <Box
            v-if="modelValue === option.key"
            align="center"
            justify="center"
            class="payment__check"
          >
            <PiIcon name="check" class="payment__check-icon" />
          </Box>
          <Box v-else class="payment__radio" />
        </Box>

        <Box
          v-if="option.key === 'wallet' && modelValue === 'wallet' && !exceedsBalance"
          align="center"
          block
          class="payment__after"
        >
          <Text type="caption" weight="semibold" color="var(--p-primary-500)" is="span">
            Balance after this order: <CurrencyText :text="formatAed(balanceAfter)" />
          </Text>
        </Box>
      </Box>
    </Box>
    <AppMessage
      v-if="modelValue === 'gateway'"
      color="warning"
      variant="outlined"
      size="small"
      message="Card payments aren't available yet. Please pay with your Platform Wallet."
    />
  </Box>
</template>

<style scoped>
.payment__option {
  width: 100%;
  padding: 18px 22px;
  border: 2px solid var(--p-surface-100);
  border-radius: 20px;
  background: var(--p-surface-0);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.payment__option--active {
  border-color: var(--p-primary-500);
  background: rgba(238, 239, 255, 0.3);
}

.payment__option:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.payment__icon {
  font-size: 24px;
  color: var(--p-blue-600);
}

.payment__text {
  min-width: 0;
}

.payment__check {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: var(--p-primary-500);
}

.payment__check-icon {
  font-size: 11px;
  color: var(--p-surface-0);
}

.payment__radio {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 2px solid var(--p-surface-300);
  border-radius: 50%;
}

.payment__after {
  padding: 7px 10px;
  border: 1px solid var(--p-primary-200);
  border-radius: 8px;
  background: var(--p-primary-50);
}
</style>
